<template>
  <div class="has-tab-bar">
    <app-bar
      title=""
      :show-back-button="true"
      back-button-path="/profile/cards"
    ></app-bar>
    <spinner-loader v-if="loading"></spinner-loader>
    <div v-if="card && card.id" class="container">
      <template v-if="mode === 'edit'">
        <!-- Header with title and status -->
        <div class="flex items-start justify-between mb-6">
          <h1 class="my-0 flex-1">Kaart bewerken</h1>
          <span
            :class="[
              card.active
                ? 'text-green-700 bg-green-50 border border-green-500'
                : 'text-gray-700 bg-gray-50 border border-gray-500',
              'rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap ml-2'
            ]"
          >
            {{ card.active ? 'Actief' : 'Inactief' }}
          </span>
        </div>

        <!-- Card info summary -->
        <div
          class="border rounded-xl p-4 mb-6 border-gray-200 bg-gray-50 text-sm"
        >
          <div class="flex justify-between mb-2">
            <span class="text-gray-600">Prijs</span>
            <span class="font-semibold">{{ $n(card.cost, 'currency') }}</span>
          </div>
          <div class="flex justify-between mb-2">
            <span class="text-gray-600">Type</span>
            <span class="font-semibold">
              {{ isUnlimited ? 'Onbeperkt' : `${total} strippen` }}
            </span>
          </div>
          <div v-if="isSeasonCard" class="flex justify-between mb-2">
            <span class="text-gray-600">Seizoen</span>
            <span class="font-semibold">
              {{ seasonStartDate }} - {{ seasonEndDate }}
            </span>
          </div>
          <div
            v-else-if="card.details && card.details.validity_period"
            class="flex justify-between mb-2"
          >
            <span class="text-gray-600">Geldigheid</span>
            <span class="font-semibold">
              {{ validityPeriodText }}
            </span>
          </div>
          <div class="flex justify-between pt-2 border-t border-gray-200">
            <span class="text-gray-600">Keer gekocht</span>
            <span class="font-semibold">
              {{ card.purchased_by ? card.purchased_by.length : 0 }}
            </span>
          </div>
        </div>

        <!-- Edit form -->
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Naam
            </label>
            <input-field
              v-model="editCard.name"
              default-value="Naam van je strippenkaart"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Beschrijving
            </label>
            <text-field
              v-model="editCard.description"
              default-value="Waarvoor kun je deze strippenkaart gebruiken? En wat zijn de voorwaarden voor gebruik?"
            />
          </div>

          <!-- Primary toggle -->
          <div class="p-3 border border-gray-200 rounded-lg">
            <toggle
              :value="editCard.is_primary"
              @toggle="editCard.is_primary = $event"
            >
              <div class="flex-1">
                <div class="text-sm font-medium text-gray-700">
                  Primaire kaart
                </div>
                <div class="text-xs text-gray-500">
                  Wordt als eerste koopoptie getoond
                </div>
              </div>
            </toggle>
          </div>

          <!-- Event selection (only for non-season cards) -->
          <div v-if="!isSeasonCard">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Gekoppelde activiteiten
            </label>
            <p class="text-xs text-gray-500 mb-2">
              Kies de activiteiten waarvoor deze strippenkaart gebruikt kan
              worden.
            </p>
            <div
              v-if="editCardEvents.length"
              class="divide-y divide-gray-200 border border-gray-200 rounded-lg mb-2"
            >
              <div
                v-for="event in editCardEvents"
                :key="event.id"
                class="py-2 px-3"
              >
                <div class="flex items-center">
                  <div class="flex-1">
                    <div class="text-sm font-medium">{{ event.name }}</div>
                    <div class="text-xs text-gray-500">
                      {{ event.nextDateFormatted }}
                    </div>
                  </div>
                  <div
                    class="cursor-pointer text-gray-400 hover:text-red-500"
                    @click="removeSelectedEvent(event.id)"
                  >
                    <icon-base size="20">
                      <icon-trash />
                    </icon-base>
                  </div>
                </div>
              </div>
            </div>
            <button
              v-if="createdUpcomingEvents.length"
              class="button button-secondary button-sm"
              @click="openSelectEvents"
            >
              {{
                editCardEvents.length
                  ? 'Activiteiten wijzigen'
                  : 'Activiteiten toevoegen'
              }}
            </button>
            <p v-else class="text-xs text-gray-500">
              Je hebt geen aankomende activiteiten om te koppelen.
            </p>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="mt-6 space-y-2">
          <button class="button button-primary w-full" @click="saveCard">
            Wijzigingen opslaan
          </button>
          <div class="flex gap-2">
            <button
              class="button button-secondary flex-1"
              @click="$router.push(`/cards/${card.id}`)"
            >
              Annuleren
            </button>
            <button
              v-if="card.active"
              class="button button-danger flex-1"
              @click="expireCard"
            >
              Inactief maken
            </button>
            <button
              v-else
              class="button button-secondary flex-1 border-green-500 text-green-700 hover:bg-green-50"
              @click="reactivateCard"
            >
              Activeren
            </button>
          </div>
        </div>
      </template>
      <template v-else>
        <!-- Header with title and status -->
        <div class="flex items-start justify-between mb-4">
          <h1 class="my-0 flex-1">{{ card.name }}</h1>
          <div class="flex gap-1 flex-shrink-0">
            <span
              v-if="card.is_primary"
              class="rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap text-blue-700 bg-blue-50 border border-blue-500"
            >
              Primair
            </span>
            <span
              :class="[
                statusClasses,
                'rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap'
              ]"
            >
              {{ statusText }}
            </span>
          </div>
        </div>

        <!-- Description and details -->
        <div class="mb-6">
          <nl2br
            v-if="card.description"
            tag="p"
            :text="card.description"
            class="text-gray-600 mb-4"
          />
          <div
            v-if="card.address"
            class="flex items-center text-sm text-gray-500"
          >
            <icon-base class="mr-2"><icon-pin /></icon-base>
            <span>{{ card.address.address }} {{ card.address.city }}</span>
          </div>
        </div>

        <!-- Order summary receipt (when not purchased and not owner) -->
        <div
          v-if="!hasPurchasedCard && !isCardOwner"
          class="bg-white rounded-2xl border border-gray-200 p-4 mb-6"
        >
          <div class="flex items-center gap-3 mb-3">
            <div
              class="flex-shrink-0 w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center"
            >
              <span class="text-xl">&#127915;</span>
            </div>
            <div class="min-w-0 flex-1">
              <h3 class="text-gray-900 text-sm truncate font-medium my-0">
                {{ isSeasonCard ? 'Seizoenskaart' : 'Strippenkaart' }}
              </h3>
              <span class="text-gray-400 text-xs">{{ card.name }}</span>
            </div>
          </div>
          <div class="border-t border-gray-100 pt-3 flex flex-col gap-1.5">
            <div
              v-if="isSeasonCard && seasonStartDate"
              class="flex items-center justify-between"
            >
              <span class="text-gray-500 text-xs">Seizoen</span>
              <span class="text-gray-700 text-xs">
                {{ seasonStartDate }} - {{ seasonEndDate }}
              </span>
            </div>
            <div
              v-else-if="validityPeriodText"
              class="flex items-center justify-between"
            >
              <span class="text-gray-500 text-xs">Geldigheid</span>
              <span class="text-gray-700 text-xs">
                {{ validityPeriodText }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-gray-500 text-xs">
                <template v-if="isUnlimited">Onbeperkt gebruik</template>
                <template v-else>{{ total }}x te gebruiken</template>
              </span>
              <span class="text-gray-700 text-xs">
                {{ $n(card.cost, 'currency') }}
              </span>
            </div>
            <div
              class="flex items-center justify-between pt-2 mt-1 border-t border-dashed border-gray-100"
            >
              <span class="text-gray-700 text-sm">Totaal</span>
              <span class="text-gray-900 font-medium text-base">
                {{ $n(card.cost, 'currency') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Owner card info -->
        <template v-if="isCardOwner">
          <div
            :class="[
              'border rounded-xl p-4 mb-4',
              card.active
                ? 'border-purple-300 bg-purple-50'
                : 'border-gray-300 bg-gray-100'
            ]"
          >
            <div class="flex justify-between items-start mb-3">
              <div>
                <div class="text-2xl font-bold text-gray-900">
                  {{ $n(card.cost, 'currency') }}
                </div>
                <div class="text-sm text-gray-600">
                  <template v-if="isUnlimited">Onbeperkt gebruik</template>
                  <template v-else>{{ total }}x te gebruiken</template>
                </div>
              </div>
              <span
                :class="[
                  card.active
                    ? 'text-green-700 bg-green-50 border border-green-500'
                    : 'text-gray-700 bg-gray-50 border border-gray-500',
                  'rounded-md px-2 py-1 text-xs font-medium'
                ]"
              >
                {{ card.active ? 'Actief' : 'Inactief' }}
              </span>
            </div>

            <!-- Season card date info -->
            <div
              v-if="isSeasonCard && card.details"
              class="text-sm text-gray-600 mb-3"
            >
              Seizoen: {{ seasonStartDate }} - {{ seasonEndDate }}
            </div>

            <!-- Validity period info -->
            <div
              v-else-if="card.details && card.details.validity_period"
              class="text-sm text-gray-600 mb-3"
            >
              {{ validityPeriodText }}
            </div>

            <!-- Stats -->
            <div
              :class="[
                'flex gap-4 pt-3 border-t',
                card.active ? 'border-purple-200' : 'border-gray-300'
              ]"
            >
              <div class="flex-1">
                <div class="text-lg font-semibold text-gray-900">
                  {{ card.purchased_by ? card.purchased_by.length : 0 }}
                </div>
                <div class="text-xs text-gray-500">keer gekocht</div>
              </div>
              <div v-if="!isUnlimited" class="flex-1">
                <div class="text-lg font-semibold text-gray-900">
                  {{ totalCreditsUsed }}
                </div>
                <div class="text-xs text-gray-500">strippen gebruikt</div>
              </div>
            </div>
          </div>

          <!-- Action buttons for owner -->
          <div class="flex flex-wrap gap-2">
            <button
              class="button button-secondary button-icon flex-1"
              :disabled="!card.purchased_by || !card.purchased_by.length"
              @click="
                card.purchased_by && card.purchased_by.length
                  ? (showPurchasedBy = true)
                  : null
              "
            >
              <icon-activity class="mr-2" />
              <span>Kaarthouders bekijken</span>
            </button>
            <button
              class="button button-primary button-icon flex-1"
              @click="$router.push(`${card.id}?mode=edit`)"
            >
              <icon-settings class="mr-2" />
              <span>Aanpassen</span>
            </button>
          </div>
        </template>

        <!-- Purchased card info with progress bar -->
        <template v-else-if="hasPurchasedCard">
          <div
            class="border rounded-xl p-4 mt-4"
            :class="
              cardIsExpired
                ? 'border-yellow-500 bg-yellow-50'
                : 'border-green-500 bg-green-50'
            "
          >
            <div class="text-sm text-gray-600 mb-3">
              Gekocht op {{ card.purchased_at }}
            </div>

            <!-- Credits visualization -->
            <template
              v-if="!cardIsExpired && (isUnlimited || card.remaining > 0)"
            >
              <template v-if="isUnlimited">
                <div class="font-semibold text-gray-900">
                  Onbeperkt gebruik t/m {{ seasonEndDate }}
                </div>
              </template>
              <template v-else>
                <div class="flex justify-between text-sm mb-2">
                  <span class="text-gray-600">Strippen</span>
                  <span class="font-semibold">
                    {{ card.remaining }} / {{ total }}
                  </span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-3">
                  <div
                    class="h-3 rounded-full transition-all"
                    :class="creditsBarColor"
                    :style="{ width: `${creditsPercentage}%` }"
                  ></div>
                </div>
              </template>
            </template>
            <template v-else>
              <div class="font-semibold text-yellow-800">
                {{ isSeasonCard ? 'Seizoenskaart' : 'Strippenkaart' }} is
                verlopen
              </div>
            </template>
          </div>
        </template>
        <template v-if="!isCardOwner && !hasPurchasedCard && card.active">
          <button
            class="w-full py-3.5 rounded-full bg-green-600 text-white hover:bg-green-700 active:bg-green-800 transition-colors flex items-center justify-center gap-2 mt-8"
            @click="gotoPayment"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span>Koop nu voor {{ $n(card.cost, 'currency') }}</span>
          </button>
          <div
            class="flex items-center justify-center gap-1.5 mt-3 text-xs text-gray-400"
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span>Veilig betalen met iDEAL of PayPal</span>
          </div>
        </template>
        <div
          v-else-if="!isCardOwner && !hasPurchasedCard && !card.active"
          class="alert alert-warning mt-8"
        >
          Deze strippenkaart is niet meer beschikbaar voor aankoop
        </div>
      </template>
    </div>
    <tab-bar selected="" />
    <modal
      :show="showPurchasedBy"
      :show-close-icon="true"
      @close="showPurchasedBy = false"
    >
      <template v-slot:header>Gekocht door</template>
      <div>
        <!-- Sort and Filter controls -->
        <div class="flex flex-wrap gap-2 mb-4 pb-4 border-b border-gray-200">
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">Sorteer:</span>
            <select
              v-model="sortField"
              class="text-sm border border-gray-300 rounded-md px-2 py-1"
            >
              <option
                v-for="option in sortOptions"
                :key="option.key"
                :value="option.key"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">Filter:</span>
            <select
              v-model="filterStatus"
              class="text-sm border border-gray-300 rounded-md px-2 py-1"
            >
              <option
                v-for="option in filterOptions"
                :key="option.key"
                :value="option.key"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Empty state -->
        <div
          v-if="filteredAndSortedPurchasedBy.length === 0"
          class="text-center text-gray-500 py-4"
        >
          Geen kaarthouders gevonden
        </div>

        <!-- User list -->
        <!--
          NOTE: `user.id` is the participant id, which repeats when the
          same person buys multiple cards. Reusing it as the v-for key
          collided in Vue's reconciliation (e.g. on card 176 nine
          participants accounted for 41 of 52 purchases) — every filter
          or sort left stale rows in the DOM and even broke the Modal's
          close button. Combine with the per-purchase timestamp so the
          key is unique per purchase.
        -->
        <div
          v-for="user in filteredAndSortedPurchasedBy"
          :key="`${user.id}-${user.purchased_at_raw || user.purchased_at}`"
          class="flex content-center my-4"
        >
          <div>
            <initials-avatar
              v-if="user.avatarUrl === null || user.avatarUrl === undefined"
              :first-name="user.firstname"
              :last-name="user.lastname"
              :size="16"
            />
            <div
              v-else
              class="mb-4 w-16 h-16 rounded-full bg-cover bg-center inline-block mr-4"
              :style="{
                backgroundImage: `url('${user.avatarUrl}')`
              }"
            />
          </div>
          <div class="flex-1">
            <div class="flex items-center gap-2">
              <h2 class="mt-0 flex-1">
                {{ user.firstname }} {{ user.lastname }}
              </h2>
              <span
                :class="[
                  'rounded-md px-1.5 py-0.5 text-xs font-medium',
                  isUserCardValid(user)
                    ? 'text-green-700 bg-green-50 border border-green-500'
                    : 'text-yellow-800 bg-yellow-50 border border-yellow-500'
                ]"
              >
                {{ isUserCardValid(user) ? 'Geldig' : 'Verlopen' }}
              </span>
            </div>
            <div class="flex text-sm text-gray-600">
              <div class="flex-1">Gekocht op: {{ user.purchased_at }}</div>
              <div v-if="isUnlimited">Onbeperkt</div>
              <div v-else>
                {{ user.details && user.details.remaining }} /
                {{ card.details && card.details.times_to_use }} resterend
              </div>
            </div>
          </div>
        </div>
      </div>
    </modal>
    <modal
      :show="showUpdateConfirmModal"
      :show-close-icon="true"
      @close="closeConfirmModal"
    >
      <template v-slot:header>Gelukt</template>
      <p>Je strippenkaart is bijgewerkt!</p>
    </modal>
    <modal
      :show="showAreYouSureModal"
      :show-close-icon="true"
      @close="showAreYouSureModal = false"
    >
      <template v-slot:header>Weet je het zeker?</template>
      <p>
        Als je deze kaart inactief maakt kan deze niet meer aangeschaft worden.
        De gebruikers die nog ongebruikte strippen hebben kunnen deze nog wel
        gebruiken tot de strippenkaart verlopen is
      </p>
      <template v-slot:footer>
        <div class="flex space-x-2 justify-between w-full">
          <button
            class="button button-secondary w-full"
            @click="showAreYouSureModal = false"
          >
            Annuleren
          </button>
          <button
            class="button button-danger w-full"
            @click="closeAreYouSureModal"
          >
            Doorgaan
          </button>
        </div>
      </template>
    </modal>
    <panel
      v-if="isMobile"
      :show="showSelectEvents"
      title="Selecteer activiteit(en)"
      @close="closeSelectEvents"
    >
      <event-select-list
        :events="createdUpcomingEvents"
        :selected-events="selectedEvents"
        @change-selected-events="changeSelectedEvents"
      />
      <button
        class="button button-primary w-full mt-8"
        @click="persistSelectedEvents"
      >
        Opslaan
      </button>
    </panel>
    <modal
      v-else
      :show="showSelectEvents"
      :show-close-icon="true"
      @close="closeSelectEvents"
    >
      <template v-slot:header>Selecteer activiteit(en)</template>
      <event-select-list
        :events="createdUpcomingEvents"
        :selected-events="selectedEvents"
        @change-selected-events="changeSelectedEvents"
      />
      <template v-slot:footer>
        <button class="button button-primary" @click="persistSelectedEvents">
          Opslaan
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import SpinnerLoader from '@/components/SpinnerLoader'
import TabBar from '@/components/TabBar'
import Modal from '@/components/Modal'
import Panel from '@/components/Panel'
import InputField from '@/components/InputField'
import TextField from '@/components/TextField'
import Toggle from '@/components/Toggle'
import InitialsAvatar from '@/components/InitialsAvatar'
import EventSelectList from '@/components/EventSelectList'
import { mapGetters, mapState } from 'vuex'
import { CREDITCARD_GET_ONE } from '@/store/actions/creditcard'
import IconBase from '@/components/IconBase.vue'
import IconPin from '@/components/icons/IconPin.vue'
import IconTrash from '@/components/icons/IconTrash.vue'
import IconActivity from '../../components/icons/IconActivity.vue'
import IconSettings from '../../components/icons/IconSettings.vue'
import Nl2br from 'vue-nl2br'
import { CREDITCARD_UPDATE } from '../../store/actions/creditcard'
import { EVENT_CREATED_BY_PARTICIPANT } from '../../store/actions/event'
import isMobile from '@/utils/isMobile'
import analytics from '@/utils/analytics'

export default {
  name: 'CreditcardDetailPage',
  components: {
    AppBar,
    SpinnerLoader,
    TabBar,
    IconBase,
    IconPin,
    IconTrash,
    IconActivity,
    IconSettings,
    Nl2br,
    Modal,
    Panel,
    InitialsAvatar,
    InputField,
    TextField,
    Toggle,
    EventSelectList
  },
  data() {
    return {
      cardId: parseInt(this.$route.params.id),
      showPurchasedBy: false,
      mode: this.$route.query.mode,
      editCard: {},
      showUpdateConfirmModal: false,
      showAreYouSureModal: false,
      origin: this.$route.query.origin,
      sortField: 'purchased_at',
      filterStatus: 'all',
      sortOptions: [
        { key: 'purchased_at', label: 'Aankoopdatum' },
        { key: 'name', label: 'Naam' },
        { key: 'remaining', label: 'Strippen over' }
      ],
      filterOptions: [
        { key: 'all', label: 'Alle' },
        { key: 'valid', label: 'Geldig' },
        { key: 'expired', label: 'Verlopen' }
      ],
      showSelectEvents: false,
      selectedEvents: [],
      isMobile: isMobile()
    }
  },
  computed: {
    ...mapGetters(['authId', 'createdUpcomingEvents']),
    ...mapState({
      card: (state) => state.creditcard.selected,
      loading: (state) => state.creditcard.loading
    }),
    isCardOwner: function () {
      return this.card.owner_id === parseInt(this.authId)
    },
    hasPurchasedCard: function () {
      return !!this.card.purchased_at
    },
    isSeasonCard: function () {
      return this.card?.type === 'season_card'
    },
    isUnlimited: function () {
      return (
        this.isSeasonCard ||
        this.card?.details?.times_to_use === null ||
        this.card?.remaining === null
      )
    },
    seasonEndDate: function () {
      const endDate = this.card?.season_end || this.card?.details?.season_end
      return endDate ? new Date(endDate).toLocaleDateString() : ''
    },
    seasonStartDate: function () {
      const startDate =
        this.card?.season_start || this.card?.details?.season_start
      return startDate ? new Date(startDate).toLocaleDateString() : ''
    },
    totalCreditsUsed: function () {
      if (!this.card?.purchased_by || this.isUnlimited) return 0
      const timesToUse = this.card?.details?.times_to_use || 0
      return this.card.purchased_by.reduce((total, user) => {
        const remaining = user.details?.remaining ?? timesToUse
        return total + (timesToUse - remaining)
      }, 0)
    },
    cardIsExpired: function () {
      if (this.isSeasonCard) {
        const seasonEnd =
          this.card?.season_end || this.card?.details?.season_end
        return seasonEnd && new Date(seasonEnd) < new Date()
      }
      return (
        new Date(this.card.valid_until) < new Date() ||
        this.card.status === 'expired'
      )
    },
    total: function () {
      return this.card?.details?.times_to_use || 0
    },
    creditsPercentage: function () {
      if (this.isUnlimited || this.total === 0) return 100
      return Math.round((this.card.remaining / this.total) * 100)
    },
    creditsBarColor: function () {
      if (this.creditsPercentage > 50) return 'bg-green-600'
      if (this.creditsPercentage > 25) return 'bg-yellow-500'
      return 'bg-red-500'
    },
    statusText: function () {
      if (this.isCardOwner) return 'Eigenaar'
      if (!this.card.active) return 'Inactief'
      if (this.hasPurchasedCard) {
        if (this.cardIsExpired) return 'Verlopen'
        if (!this.isUnlimited && this.card.remaining === 0) return 'Op'
        return 'Actief'
      }
      return 'Beschikbaar'
    },
    statusClasses: function () {
      const statusMap = {
        Eigenaar: 'text-purple-700 bg-purple-50 border border-purple-500',
        Actief: 'text-green-700 bg-green-50 border border-green-500',
        Verlopen: 'text-yellow-800 bg-yellow-50 border border-yellow-500',
        Op: 'text-red-700 bg-red-50 border border-red-500',
        Inactief: 'text-gray-700 bg-gray-50 border border-gray-500',
        Beschikbaar: 'text-blue-700 bg-blue-50 border border-blue-500'
      }
      return statusMap[this.statusText] || statusMap['Beschikbaar']
    },
    validityPeriodText: function () {
      const period = this.card?.details?.validity_period
      if (period === 'indefinitely') return 'Onbeperkt geldig na aankoop'
      if (period === '1,year') return '1 jaar geldig na aankoop'
      if (period === '6,month') return 'Een half jaar geldig na aankoop'
      return ''
    },
    editCardEvents: function () {
      if (!this.editCard.details || !this.editCard.details.events) return []
      return this.editCard.details.events
        .map((event) => {
          // If event is just an ID, find the full event data
          if (typeof event === 'number' || typeof event === 'string') {
            return this.createdUpcomingEvents.find(
              (e) => e.id === parseInt(event)
            )
          }
          // If event has id but no name, enrich it
          if (event.id && !event.name) {
            const fullEvent = this.createdUpcomingEvents.find(
              (e) => e.id === event.id
            )
            return fullEvent || event
          }
          return event
        })
        .filter((event) => event && event.name)
    },
    filteredAndSortedPurchasedBy: function () {
      if (!this.card?.purchased_by) return []

      let filtered = [...this.card.purchased_by]

      // Filter by validity status
      if (this.filterStatus === 'valid') {
        filtered = filtered.filter((user) => this.isUserCardValid(user))
      } else if (this.filterStatus === 'expired') {
        filtered = filtered.filter((user) => !this.isUserCardValid(user))
      }

      // Sort
      filtered.sort((a, b) => {
        if (this.sortField === 'purchased_at') {
          const dateA = new Date(a.purchased_at_raw || a.purchased_at)
          const dateB = new Date(b.purchased_at_raw || b.purchased_at)
          return dateB - dateA // Newest first
        } else if (this.sortField === 'name') {
          const nameA = `${a.firstname} ${a.lastname}`.toLowerCase()
          const nameB = `${b.firstname} ${b.lastname}`.toLowerCase()
          return nameA.localeCompare(nameB)
        } else if (this.sortField === 'remaining') {
          const remainingA = a.details?.remaining ?? Infinity
          const remainingB = b.details?.remaining ?? Infinity
          return remainingB - remainingA // Most remaining first
        }
        return 0
      })

      return filtered
    }
  },
  watch: {
    card(card) {
      // Normalize events to IDs only
      const events = (card.details?.events || []).map((event) =>
        typeof event === 'object' ? event.id : event
      )
      this.editCard = {
        ...card,
        details: {
          ...card.details,
          events
        }
      }
    },
    '$route.query.mode'(m) {
      this.mode = m
    }
  },
  created: function () {
    this.$store.dispatch(CREDITCARD_GET_ONE, {
      creditcardId: this.cardId
    })
    this.$store.dispatch(EVENT_CREATED_BY_PARTICIPANT)
    analytics.capture('creditcard_viewed', { creditcard_id: this.cardId })
  },
  methods: {
    gotoPayment: function () {
      if (!this.card || !this.card.active) return
      this.$router.push({
        path: `/cards/${this.cardId}/purchase`,
        ...(this.origin && { query: { origin: this.origin } })
      })
    },
    saveCard: function () {
      this.$store.dispatch(CREDITCARD_UPDATE, this.editCard)
      this.$store.dispatch(CREDITCARD_GET_ONE, {
        creditcardId: this.cardId
      })
      this.showUpdateConfirmModal = true
    },
    closeConfirmModal: function () {
      this.showUpdateConfirmModal = false
      this.$router.push(`/cards/${this.cardId}`)
    },
    expireCard: function () {
      this.showAreYouSureModal = true
    },
    closeAreYouSureModal: function () {
      this.showAreYouSureModal = false
      this.$store.dispatch(CREDITCARD_UPDATE, {
        ...this.card,
        active: false
      })
      this.$router.push(`/cards/${this.cardId}`)
    },
    reactivateCard: function () {
      this.$store.dispatch(CREDITCARD_UPDATE, {
        ...this.card,
        active: true
      })
      this.$store.dispatch(CREDITCARD_GET_ONE, {
        creditcardId: this.cardId
      })
      this.showUpdateConfirmModal = true
    },
    isUserCardValid: function (user) {
      // Check if season card has expired
      if (this.isSeasonCard) {
        const seasonEnd =
          user.details?.season_end || this.card?.details?.season_end
        if (seasonEnd && new Date(seasonEnd) < new Date()) {
          return false
        }
      } else {
        // Check if regular card has expired
        const validUntil = user.details?.valid_until
        if (validUntil && new Date(validUntil) < new Date()) {
          return false
        }
        if (user.details?.status === 'expired') {
          return false
        }
      }

      // Check if card has remaining credits (unless unlimited)
      const isUserUnlimited =
        this.isSeasonCard || user.details?.remaining === null
      if (!isUserUnlimited && user.details?.remaining === 0) {
        return false
      }

      return user.details?.status === 'active'
    },
    openSelectEvents: function () {
      // Use editCardEvents to get full event objects for the selection
      this.selectedEvents = [...this.editCardEvents]
      this.showSelectEvents = true
    },
    closeSelectEvents: function () {
      this.selectedEvents = []
      this.showSelectEvents = false
    },
    changeSelectedEvents: function (selectedEvents) {
      this.selectedEvents = [...selectedEvents]
    },
    persistSelectedEvents: function () {
      this.editCard = {
        ...this.editCard,
        details: {
          ...this.editCard.details,
          events: this.selectedEvents.map((event) => event.id)
        }
      }
      this.showSelectEvents = false
    },
    removeSelectedEvent: function (id) {
      this.editCard = {
        ...this.editCard,
        details: {
          ...this.editCard.details,
          events: (this.editCard.details?.events || []).filter((eventId) => {
            const actualId = typeof eventId === 'object' ? eventId.id : eventId
            return actualId !== id
          })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
</style>
